import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Button, Legend, Radio, RadioGroup } from "@boozallen-uip/uswds-react-core";
import SearchableSelect from "../../../components/SearchableSelect.component";
import {
  PRODUCT_VISIBILITY_BUYERS_ONLY,
  PRODUCT_VISIBILITY_HIDE,
  PRODUCT_VISIBILITY_PUBLIC,
} from "../../../utlities/core.constants";
import {
  FormField,
  FormFieldBody,
  FormFieldContainer,
  FormSectionContainer,
  FormSectionHeader,
  FormSectionHeaderContainer,
  FormSectionSubHeader,
} from "../../shared.styled";

export default function ProductVisibilitySection({
  normalizedProductVisibility,
  clearFieldErrors,
  setProduct,
  getFieldError,
  renderInlineFieldError,
  FormFieldRequired,
  agencyExclusionsApply,
  selectedHiddenAgencies,
  product,
  agencies,
  hiddenAgencySelectionAnnouncement,
  agencyLoadError,
  fieldIds,
  sectionClassName,
  fieldContainerClassName,
  showSectionHeader,
  readOnly,
}) {
  const lastSelectedVisibilityRef = useRef(normalizedProductVisibility);
  const hasVisibilityChangedRef = useRef(false);
  const visibilityFieldRef = useRef(null);

  useEffect(() => {
    if (!hasVisibilityChangedRef.current) {
      return;
    }
    const selectedValue = lastSelectedVisibilityRef.current;
    if (!selectedValue) {
      return;
    }
    const selectedRadio = visibilityFieldRef.current?.querySelector(
      `input[name="${fieldIds.visibility}"][value="${selectedValue}"]`
    );
    if (selectedRadio && document.activeElement !== selectedRadio) {
      selectedRadio.focus();
    }
  }, [fieldIds.visibility, normalizedProductVisibility]);

  return (
    <FormSectionContainer className={sectionClassName}>
      {showSectionHeader ? (
        <FormSectionHeaderContainer>
          <FormSectionHeader>Product Visibility</FormSectionHeader>
          <FormSectionSubHeader className="mb-0">
            <p className="mb-2">Choose who can see this product listing.</p>
            <ul className="list-disc pl-5 mb-0">
              <li><strong>Public:</strong> Visible to public, buyer, and vendor users.</li>
              <li>
                <strong>Buyers-only (Government-only):</strong> Visible to buyer accounts and anyone in your vendor organization.
                When you select this option, additional fields appear so you can hide this listing from specific government agencies if needed.
              </li>
              <li><strong>Hidden:</strong> Only visible to users within your vendor organization.</li>
            </ul>
          </FormSectionSubHeader>
        </FormSectionHeaderContainer>
      ) : null}
      <FormFieldContainer className={fieldContainerClassName}>
        <FormFieldBody>
          <FormField ref={visibilityFieldRef} className="col-start-1 col-span-12 mt-4">
            <RadioGroup
              id={fieldIds.visibility}
              name={fieldIds.visibility}
              value={normalizedProductVisibility}
              onChange={(e) => {
                if (readOnly) return;
                const nextVisibility = e.detail.value;
                lastSelectedVisibilityRef.current = nextVisibility;
                hasVisibilityChangedRef.current = true;
                clearFieldErrors(["product.visibility"]);
                setProduct((prevProduct) => ({
                  ...prevProduct,
                  visibility: nextVisibility,
                  hiddenAgencyIds:
                    nextVisibility === PRODUCT_VISIBILITY_BUYERS_ONLY
                      ? (prevProduct.hiddenAgencyIds || [])
                      : [],
                }));
              }}
              required
            >
              <Legend>Product visibility control <FormFieldRequired /></Legend>
              <Radio value={PRODUCT_VISIBILITY_PUBLIC} disabled={readOnly}>Public</Radio>
              <Radio value={PRODUCT_VISIBILITY_BUYERS_ONLY} disabled={readOnly}>Buyers-only (Government-only)</Radio>
              <Radio value={PRODUCT_VISIBILITY_HIDE} disabled={readOnly}>Hidden</Radio>
            </RadioGroup>
            {renderInlineFieldError(getFieldError("product.visibility"))}
          </FormField>
          {agencyExclusionsApply ? (
            <FormField className="col-start-1 col-span-12">
              <div
                className="mt-3 rounded-md border border-base-light bg-base-lightest p-4 shadow-sm"
                role="group"
                aria-labelledby="hiddenAgencyIds-label"
              >
                <div className="mb-3 flex flex-wrap items-start justify-between gap-3 border-b border-base-light pb-3">
                  <span className="usa-label m-0 block max-w-full text-base-darker" id="hiddenAgencyIds-label">
                    Agencies this product is hidden from
                  </span>
                </div>
                <p id="hiddenAgencyIds-helper" className="m-0 mb-3 max-w-full text-sm leading-normal text-base-dark">
                  Optional. Government agency accounts you add here <span className="font-semibold text-base-darker">cannot find or open this listing</span> while signed in to this application (search and direct product link).
                </p>
                <SearchableSelect
                  id={fieldIds.hiddenAgencyIds}
                  options={agencies.map((agency) => ({ value: String(agency.id), label: agency.name }))}
                  value={product.hiddenAgencyIds || []}
                  onChange={(hiddenAgencyIds) => setProduct((prevProduct) => ({ ...prevProduct, hiddenAgencyIds }))}
                  disabled={readOnly}
                  multiple
                  fullWidth
                  placeholder="Search and add agencies..."
                  clearButtonLabel="Clear selected agencies"
                  filterPlaceholder="Type to filter agencies..."
                  noResultsText="No agencies found"
                  listAriaLabel="Agency options"
                  ariaDescribedBy="hiddenAgencyIds-helper"
                />
                <div className="mt-3">
                  <Button
                    type="button"
                    outline
                    className="m-0 shrink-0"
                    disabled={readOnly || selectedHiddenAgencies.length === 0}
                    onClick={() => setProduct((prev) => ({ ...prev, hiddenAgencyIds: [] }))}
                    aria-label="Clear all selected agencies"
                  >
                    Clear all
                  </Button>
                </div>
                <div
                  id="hiddenAgencyIds-selection-status"
                  className="usa-sr-only"
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {hiddenAgencySelectionAnnouncement}
                </div>
                {selectedHiddenAgencies.length > 0 ? (
                  <div className="mt-4" role="region" aria-label="Agencies hidden from this listing">
                    <p className="m-0 mb-2 text-xs font-semibold uppercase tracking-wide text-base-dark">
                      Selected ({selectedHiddenAgencies.length})
                    </p>
                    <ul className="m-0 flex flex-wrap gap-2 p-0 list-none">
                      {selectedHiddenAgencies.map((agency) => (
                        <li
                          key={agency.id}
                          className="inline-flex items-center gap-2 rounded border border-base-light bg-white px-2 py-1 text-sm shadow-sm"
                        >
                          <span>{agency.name || `Agency ID: ${agency.id}`}</span>
                          {!readOnly ? (
                            <button
                              type="button"
                              className="inline-flex min-h-6 min-w-6 shrink-0 items-center justify-center rounded-full border border-base-light text-base-dark hover:bg-base-lighter focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-[#2491ff]"
                              onClick={() => setProduct((prev) => ({
                                ...prev,
                                hiddenAgencyIds: (prev.hiddenAgencyIds || []).filter((id) => String(id) !== agency.id),
                              }))}
                              aria-label={`Remove ${agency.name || `agency ${agency.id}`} from hidden list`}
                            >
                              <span aria-hidden="true">&times;</span>
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="m-0 mt-4 text-sm text-base-dark">
                    No agencies selected yet. Use the field above to add one or more.
                  </p>
                )}
                {agencyLoadError ? (
                  <p className="m-0 mt-3 text-sm text-red" role="status" aria-live="polite">
                    {agencyLoadError}
                  </p>
                ) : null}
              </div>
            </FormField>
          ) : null}
        </FormFieldBody>
      </FormFieldContainer>
    </FormSectionContainer>
  );
}

ProductVisibilitySection.propTypes = {
  normalizedProductVisibility: PropTypes.string.isRequired,
  clearFieldErrors: PropTypes.func.isRequired,
  setProduct: PropTypes.func.isRequired,
  getFieldError: PropTypes.func.isRequired,
  renderInlineFieldError: PropTypes.func.isRequired,
  FormFieldRequired: PropTypes.elementType.isRequired,
  agencyExclusionsApply: PropTypes.bool.isRequired,
  selectedHiddenAgencies: PropTypes.array.isRequired,
  product: PropTypes.object.isRequired,
  agencies: PropTypes.array.isRequired,
  hiddenAgencySelectionAnnouncement: PropTypes.string.isRequired,
  agencyLoadError: PropTypes.string,
  fieldIds: PropTypes.shape({
    visibility: PropTypes.string.isRequired,
    hiddenAgencyIds: PropTypes.string.isRequired,
  }),
  sectionClassName: PropTypes.string,
  fieldContainerClassName: PropTypes.string,
  showSectionHeader: PropTypes.bool,
  readOnly: PropTypes.bool,
};

ProductVisibilitySection.defaultProps = {
  agencyLoadError: null,
  fieldIds: {
    visibility: "productVisibility",
    hiddenAgencyIds: "hiddenAgencyIds",
  },
  sectionClassName: "col-span-8 !mb-5",
  fieldContainerClassName: "",
  showSectionHeader: true,
  readOnly: false,
};
